import { IdParams } from '../dto/common.js';
import { DocIdParams, DocTitleV1 } from '../dto/documents.js';
import { listOf } from '../dto/list.js';
import { ExportCreateRequestV1, ExportV1, RecentQueryV1, RenderedV1, RenderQueryV1, SettingParams, SettingUpdateRequestV1, SidebarAddRequestV1, SidebarItemV1, SidebarUpdateRequestV1, } from '../dto/workspace.js';
import { defaultSettings, SettingsV1 } from '../registries/settings.js';
import { defineRoute } from '../route/define-route.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_ANSWER_IDS } from './examples.js';
import { EXAMPLE_TITLES } from './examples-docs.js';
import { EXAMPLE_EXPORT, EXAMPLE_RENDERED, EXAMPLE_SIDEBAR_ADDED, EXAMPLE_SIDEBAR_GROUP, EXAMPLE_SIDEBAR_PIN, } from './examples-workspace.js';
/**
 * The workspace routes of exit 1a (1.0b deliverable 17): Recent, settings and the sidebar (1.V-1),
 * rendering (1.W stage 1) and exports on demand (1.P; decision 32, no nightly export).
 */
const session = { status: 'frozen', auth: 'session', since: 'exit-1a' };
const pin = EXAMPLE_IDS.sidebarPinSaltInTheWells.id;
/**
 * The canon documents opened last, newest first (A2.1 section 4: Recent lists canon only), at
 * most `limit`. Its path is literal under `/api/docs/:id`, so the table lists it before `docs.get`.
 */
export const docsRecent = defineRoute({
    id: 'docs.recent',
    method: 'GET',
    path: '/api/docs/recent',
    query: RecentQueryV1,
    response: { kind: 'json', schema: listOf(DocTitleV1) },
    reference: 'none',
    owner: '1.V-1',
    ...session,
    examples: {
        request: { query: { limit: 10 } },
        response: { items: [EXAMPLE_TITLES[0]], next: null },
    },
});
/**
 * Record that the GM opened a canon document (`last_opened_at`): the web app calls it on opening,
 * so no read route writes.
 */
export const docsMarkOpened = defineRoute({
    id: 'docs.markOpened',
    method: 'POST',
    path: '/api/docs/:id/opened',
    params: DocIdParams,
    response: { kind: 'empty' },
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    owner: '1.V-1',
    ...session,
    examples: {
        request: { params: { id: EXAMPLE_IDS.questMissingLamplighters.id } },
        response: null,
    },
});
/** Every setting, its default where none is stored. */
export const settingsGet = defineRoute({
    id: 'settings.get',
    method: 'GET',
    path: '/api/settings',
    response: { kind: 'json', schema: SettingsV1 },
    reference: 'none',
    owner: '1.V-1',
    ...session,
    examples: { response: defaultSettings() },
});
/**
 * Write one setting. A key outside `SETTINGS` answers 404; a value its key's schema refuses 400
 * `invalid_request` with the issues.
 */
export const settingsUpdate = defineRoute({
    id: 'settings.update',
    method: 'PUT',
    path: '/api/settings/:key',
    params: SettingParams,
    body: SettingUpdateRequestV1,
    response: { kind: 'json', schema: SettingsV1 },
    reference: 'none',
    owner: '1.V-1',
    ...session,
    examples: {
        request: { params: { key: 'size_cap_words' }, body: { value: 25_000 } },
        response: { ...defaultSettings(), size_cap_words: 25_000 },
    },
});
/** The sidebar's pins and groups (a bounded list). */
export const sidebarList = defineRoute({
    id: 'sidebar.list',
    method: 'GET',
    path: '/api/sidebar',
    response: { kind: 'json', schema: listOf(SidebarItemV1) },
    reference: 'none',
    owner: '1.V-1',
    ...session,
    examples: { response: { items: [EXAMPLE_SIDEBAR_PIN, EXAMPLE_SIDEBAR_GROUP], next: null } },
});
/** Pin a document (a reference page included: a pin is a target) or a view, or add a group. */
export const sidebarAdd = defineRoute({
    id: 'sidebar.add',
    method: 'POST',
    path: '/api/sidebar/items',
    body: SidebarAddRequestV1,
    response: { kind: 'json', schema: SidebarItemV1, status: 201 },
    reference: 'target-ok',
    docIds: { target: ['body.refId'] },
    owner: '1.V-1',
    ...session,
    examples: {
        request: {
            body: {
                kind: 'document',
                refId: EXAMPLE_IDS.npcMaelisDorn.id,
                parentId: EXAMPLE_IDS.sidebarGroupPrep.id,
            },
        },
        response: EXAMPLE_SIDEBAR_ADDED,
    },
});
export const sidebarUpdate = defineRoute({
    id: 'sidebar.update',
    method: 'PATCH',
    path: '/api/sidebar/items/:id',
    params: IdParams,
    body: SidebarUpdateRequestV1,
    response: { kind: 'json', schema: SidebarItemV1 },
    reference: 'none',
    owner: '1.V-1',
    ...session,
    examples: {
        request: { params: { id: pin }, body: { parentId: EXAMPLE_IDS.sidebarGroupPrep.id } },
        response: { ...EXAMPLE_SIDEBAR_PIN, parentId: EXAMPLE_IDS.sidebarGroupPrep.id },
    },
});
export const sidebarRemove = defineRoute({
    id: 'sidebar.remove',
    method: 'DELETE',
    path: '/api/sidebar/items/:id',
    params: IdParams,
    response: { kind: 'empty' },
    reference: 'none',
    owner: '1.V-1',
    ...session,
    examples: { request: { params: { id: pin } }, response: null, consumes: true },
});
/** A document rendered for Read mode (`html`), the table-read (`table`) or Markdown. */
export const renderDoc = defineRoute({
    id: 'render.doc',
    method: 'GET',
    path: '/api/docs/:id/render',
    params: DocIdParams,
    query: RenderQueryV1,
    response: { kind: 'json', schema: RenderedV1 },
    reference: 'read',
    docIds: { owner: ['params.id'] },
    owner: '1.W',
    stage: '1',
    ...session,
    examples: {
        request: {
            params: { id: EXAMPLE_IDS.questMissingLamplighters.id },
            query: { target: 'table' },
        },
        response: EXAMPLE_RENDERED,
    },
});
/** Start an export of the campaign (canon only), on demand: 202 with its queued job. */
export const exportsCreate = defineRoute({
    id: 'exports.create',
    method: 'POST',
    path: '/api/exports',
    body: ExportCreateRequestV1,
    response: { kind: 'json', schema: ExportV1, status: 202 },
    reference: 'none',
    owner: '1.P',
    ...session,
    examples: { request: { body: { format: 'markdown' } }, response: EXAMPLE_EXPORT },
});
const exportId = { params: { id: EXAMPLE_IDS.exportMarkdown.id } };
const noExportYet = 'an export exists only once its job ran, and a seeded database holds no export job';
/** An export's state; `unrenderedPages` is read from the zip's manifest once it succeeded. */
export const exportsGet = defineRoute({
    id: 'exports.get',
    method: 'GET',
    path: '/api/exports/:id',
    params: IdParams,
    response: { kind: 'json', schema: ExportV1 },
    reference: 'none',
    owner: '1.P',
    ...session,
    examples: {
        request: exportId,
        response: {
            ...EXAMPLE_EXPORT,
            status: 'succeeded',
            finishedAt: '2026-09-12T19:00:04.000Z',
            assetId: EXAMPLE_ANSWER_IDS.exportFile,
            bytes: 48_211,
            unrenderedPages: 0,
        },
        success: { skip: noExportYet },
    },
});
/** An export's file; before its job finished, 409 `export_not_ready`. */
export const exportsFile = defineRoute({
    id: 'exports.file',
    method: 'GET',
    path: '/api/exports/:id/file',
    params: IdParams,
    response: { kind: 'file' },
    errors: { 409: ['export_not_ready'] },
    reference: 'none',
    owner: '1.P',
    ...session,
    examples: {
        request: exportId,
        response: { contentType: 'application/zip', body: '' },
        success: { skip: noExportYet },
    },
});
//# sourceMappingURL=workspace.js.map